import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChartBar } from "@phosphor-icons/react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { MediaOrganizerGrid } from "@/components/MediaOrganizerGrid";
import { NewsroomAdminNav } from "@/components/NewsroomAdminNav";
import { NewsroomControlPanels } from "@/components/NewsroomControlPanels";
import { Button } from "@/components/ui/button";
import { usePageSeo } from "@/hooks/usePageSeo";
import { getStorySectionLabel } from "@/utils/storySections";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLACEHOLDER_THUMBNAIL = "https://customer-assets.emergentagent.com/job_content-watch-7/artifacts/thy81wjj_747B42A6-4C25-4A17-A557-B9BBE91C99DB%20Background%20Removed.png";
const SECTION_OPTIONS = [
  { value: "breaking", label: "Breaking News" },
  { value: "crime", label: "Crime" },
  { value: "public-safety", label: "Public Safety" },
  { value: "politics", label: "Politics" },
  { value: "investigations", label: "Investigations" },
  { value: "public-records", label: "Public Records" },
  { value: "community", label: "Community" },
  { value: "weather", label: "Weather" },
  { value: "traffic", label: "Traffic" },
  { value: "videos", label: "Videos" },
  { value: "live", label: "Live" },
  { value: "sports", label: "Sports" },
  { value: "editorial", label: "What’s New" },
];

const getDeskStatus = (story) => {
  if (story.content_type === "live") return "live on homepage";
  if (story.was_live) return "ended live stream";
  return story.platform;
};

const buildDefaultPhotoCredit = (author) => (author ? `Photo courtesy of ${author}` : "");

const createMediaItem = (file, extra = {}) => ({
  id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
  file,
  name: file.name,
  previewUrl: URL.createObjectURL(file),
  ...extra,
});

const createExistingMediaItem = (assetId, previewUrl, name, extra = {}) => ({
  id: `existing-${assetId || name}-${Math.random().toString(36).slice(2, 8)}`,
  assetId,
  name,
  previewUrl,
  ...extra,
});

const reorderMediaItems = (items, sourceId, targetId) => {
  const sourceIndex = items.findIndex((item) => item.id === sourceId);
  const targetIndex = items.findIndex((item) => item.id === targetId);
  if (sourceIndex === -1 || targetIndex === -1) {
    return items;
  }
  const updatedItems = [...items];
  const [movedItem] = updatedItems.splice(sourceIndex, 1);
  updatedItems.splice(targetIndex, 0, movedItem);
  return updatedItems;
};

const countParagraphBlocks = (value) => {
  if (!value || typeof window === "undefined") {
    return 1;
  }
  const parser = new window.DOMParser();
  const parsedDocument = parser.parseFromString(value, "text/html");
  const blockCount = parsedDocument.body.querySelectorAll("p, li, blockquote, h1, h2, h3").length;
  if (blockCount > 0) {
    return blockCount;
  }
  return parsedDocument.body.textContent?.trim() ? 1 : 1;
};

const toDateTimeLocal = (value) => {
  if (!value) {
    return "";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  const offsetDate = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const renderMediaPreview = (title, images) => {
  if (!images?.length) {
    return null;
  }

  return (
    <div className="space-y-3">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">{title}</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {images.map((imageUrl, index) => (
          <div key={`${title}-${imageUrl}-${index}`} className="overflow-hidden border border-[rgba(10,15,26,0.12)] bg-slate-100" data-testid={`office-use-existing-media-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${index}`}>
            <div className="aspect-[4/3] w-full">
              <img alt={`${title} ${index + 1}`} className="h-full w-full object-cover" src={imageUrl} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const OfficeUsePage = () => {
  usePageSeo({
    canonicalPath: "/office-use",
    description: "Content Watch HQ private newsroom editor workspace.",
    robots: "noindex,nofollow",
    title: "Office Use | Content Watch HQ",
  });

  const navigate = useNavigate();
  const [headline, setHeadline] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [photoCredit, setPhotoCredit] = useState("");
  const [photoCreditCustomized, setPhotoCreditCustomized] = useState(false);
  const [section, setSection] = useState("breaking");
  const [location, setLocation] = useState("Houston, Texas");
  const [tags, setTags] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [publishedAt, setPublishedAt] = useState(() => toDateTimeLocal(new Date().toISOString()));
  const [mainImage, setMainImage] = useState(null);
  const [supportingImages, setSupportingImages] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [thumbnailImage, setThumbnailImage] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [managedStories, setManagedStories] = useState([]);
  const [archivedStories, setArchivedStories] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loadingControlRoom, setLoadingControlRoom] = useState(false);
  const [deletingSlug, setDeletingSlug] = useState(null);
  const [restoringArchiveId, setRestoringArchiveId] = useState(null);
  const [editingStorySlug, setEditingStorySlug] = useState(null);
  const [editingStoryPreview, setEditingStoryPreview] = useState(null);

  const token = window.localStorage.getItem("content_watch_token");
  const canDeleteStories = Boolean(currentUser?.can_delete);
  const canEditStories = Boolean(currentUser?.can_edit);
  const paragraphCount = useMemo(() => countParagraphBlocks(bodyHtml), [bodyHtml]);
  const paragraphOptions = useMemo(() => Array.from({ length: Math.max(paragraphCount, 1) }, (_, index) => index + 1), [paragraphCount]);

  const resetComposer = useCallback(() => {
    setHeadline("");
    setSection("breaking");
    setLocation("Houston, Texas");
    setTags("");
    setYoutubeUrl("");
    setBodyHtml("");
    setPublishedAt(toDateTimeLocal(new Date().toISOString()));
    setMainImage(null);
    setSupportingImages([]);
    setGalleryImages([]);
    setThumbnailImage(null);
    setEditingStorySlug(null);
    setEditingStoryPreview(null);
    setPhotoCreditCustomized(false);
  }, []);

  const loadManagedStories = useCallback(async () => {
    const storiesResponse = await axios.get(`${API_BASE}/newsfeed`);
    setManagedStories(storiesResponse.data.slice(0, 24));
  }, []);

  const loadControlRoom = useCallback(async (authToken = token, permissions = {}) => {
    if (!authToken) {
      return;
    }

    const canEdit = typeof permissions.can_edit === "boolean" ? permissions.can_edit : canEditStories;
    const canDelete = typeof permissions.can_delete === "boolean" ? permissions.can_delete : canDeleteStories;

    setLoadingControlRoom(true);
    try {
      await loadManagedStories();
      if (canEdit) {
        const analyticsResponse = await axios.get(`${API_BASE}/office/analytics`, { headers: { Authorization: `Bearer ${authToken}` } });
        setAnalytics(analyticsResponse.data);
      }
      if (canDelete) {
        const archiveResponse = await axios.get(`${API_BASE}/office/archive`, { headers: { Authorization: `Bearer ${authToken}` } });
        setArchivedStories(archiveResponse.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Story controls could not load.");
    } finally {
      setLoadingControlRoom(false);
    }
  }, [canDeleteStories, canEditStories, loadManagedStories, token]);

  useEffect(() => {
    if (!photoCreditCustomized) {
      setPhotoCredit(buildDefaultPhotoCredit(authorName));
    }
  }, [authorName, photoCreditCustomized]);

  useEffect(() => {
    setSupportingImages((currentItems) => currentItems.map((item) => ({
      ...item,
      insertAfterParagraph: Math.min(item.insertAfterParagraph || 1, Math.max(paragraphCount, 1)),
    })));
  }, [paragraphCount]);

  useEffect(() => {
    if (!token) {
      navigate("/newsroom-login");
      return;
    }
    axios.get(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).then((response) => {
      setCurrentUser(response.data);
      setAuthorName((existingAuthor) => existingAuthor || response.data.first_name || response.data.full_name || "");
      if (!photoCreditCustomized) {
        setPhotoCredit((existingCredit) => existingCredit || buildDefaultPhotoCredit(response.data.first_name || response.data.full_name || ""));
      }
      if (response.data.can_edit || response.data.can_delete) {
        loadControlRoom(token, response.data);
      }
    }).catch(() => {
      window.localStorage.removeItem("content_watch_token");
      navigate("/newsroom-login");
    });
  }, [loadControlRoom, navigate, photoCreditCustomized, token]);

  const mainImageName = useMemo(() => mainImage?.name || "", [mainImage]);
  const supportingImageNames = useMemo(() => supportingImages.map((item) => item.name), [supportingImages]);
  const galleryImageNames = useMemo(() => galleryImages.map((item) => item.name), [galleryImages]);
  const thumbnailImageName = useMemo(() => thumbnailImage?.name || "", [thumbnailImage]);

  const populateFormForEditing = (story) => {
    setEditingStorySlug(story.slug);
    setEditingStoryPreview(story);
    setHeadline(story.headline || "");
    setAuthorName(story.author || currentUser?.first_name || currentUser?.full_name || "");
    setPhotoCredit(story.photo_credit || buildDefaultPhotoCredit(story.author || currentUser?.first_name || currentUser?.full_name || ""));
    setPhotoCreditCustomized(Boolean(story.photo_credit));
    setSection(story.section || "breaking");
    setLocation(story.location || "Houston, Texas");
    setTags((story.tags || []).join(", "));
    setYoutubeUrl(story.video_url || story.permalink || "");
    setBodyHtml(story.body_html || "");
    setPublishedAt(toDateTimeLocal(story.published_at));
    setMainImage(story.image_url ? createExistingMediaItem(story.hero_asset_id || story.cover_asset_id, story.image_url, "Current hero image") : null);
    setSupportingImages((story.supporting_media_items || []).map((item, index) => createExistingMediaItem(item.asset_id, item.url, `Current supporting image ${index + 1}`, { insertAfterParagraph: item.insert_after_paragraph || 1 })));
    setGalleryImages((story.summary_image_gallery || []).map((imageUrl, index) => createExistingMediaItem(story.summary_image_asset_ids?.[index], imageUrl, `Current gallery image ${index + 1}`)));
    setThumbnailImage(story.thumbnail_url ? createExistingMediaItem(story.thumbnail_asset_id, story.thumbnail_url, "Current thumbnail image") : null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePublish = async (event) => {
    event.preventDefault();
    setPublishing(true);
    try {
      const formData = new FormData();
      formData.append("headline", headline);
      formData.append("author", authorName);
      formData.append("photo_credit", photoCredit);
      formData.append("section", section);
      formData.append("location", location);
      formData.append("tags", tags);
      formData.append("youtube_url", youtubeUrl);
      formData.append("body_html", bodyHtml);
      formData.append("published_at", publishedAt);
      if (editingStorySlug) {
        formData.append("existing_hero_asset_id", mainImage?.assetId || "");
        formData.append("existing_thumbnail_asset_id", thumbnailImage?.assetId || "");
        formData.append("existing_supporting_media_items", JSON.stringify(supportingImages.filter((item) => item.assetId).map((item) => ({ asset_id: item.assetId, insert_after_paragraph: item.insertAfterParagraph || 1 }))));
        formData.append("existing_summary_image_asset_ids", JSON.stringify(galleryImages.filter((item) => item.assetId).map((item) => item.assetId)));
      }
      if (mainImage) {
        if (mainImage.file) {
          formData.append("main_image", mainImage.file);
        }
      }
      supportingImages.filter((item) => item.file).forEach((item) => formData.append("supporting_images", item.file));
      formData.append("supporting_image_layout", JSON.stringify(supportingImages.map((item) => item.insertAfterParagraph || 1)));
      galleryImages.filter((item) => item.file).forEach((item) => formData.append("gallery_images", item.file));
      if (thumbnailImage) {
        if (thumbnailImage.file) {
          formData.append("thumbnail_image", thumbnailImage.file);
        }
      }

      const request = editingStorySlug
        ? axios.put(`${API_BASE}/office/stories/${editingStorySlug}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        })
        : axios.post(`${API_BASE}/office/stories`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      const response = await request;

      if (canEditStories) {
        await loadManagedStories();
      }
      if (canDeleteStories) {
        await loadControlRoom(token);
      }

      if (editingStorySlug) {
        toast.success("Story updated.");
        resetComposer();
        setAuthorName(currentUser?.first_name || currentUser?.full_name || "");
        return;
      }

      toast.success("Story published.");
      resetComposer();
      setAuthorName(currentUser?.first_name || currentUser?.full_name || "");
      navigate(`/stories/${response.data.slug}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Publishing did not complete.");
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteStory = async (slug) => {
    const confirmed = window.confirm("This will remove the story from the website and move it into Colton's private archive. Continue?");
    if (!confirmed) {
      return;
    }

    setDeletingSlug(slug);
    try {
      const response = await axios.delete(`${API_BASE}/office/stories/${slug}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setManagedStories((currentStories) => currentStories.filter((story) => story.slug !== slug));
      setArchivedStories((currentStories) => [response.data, ...currentStories.filter((story) => story.slug !== slug)]);
      toast.success("Story removed from the site and archived for Colton.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete did not complete.");
    } finally {
      setDeletingSlug(null);
    }
  };

  const handleRestoreStory = async (archiveId) => {
    const confirmed = window.confirm("This will move the archived story back into the live site immediately. Continue?");
    if (!confirmed) {
      return;
    }

    setRestoringArchiveId(archiveId);
    try {
      const response = await axios.post(
        `${API_BASE}/office/archive/${archiveId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setArchivedStories((currentStories) => currentStories.filter((story) => story.archive_id !== archiveId));
      setManagedStories((currentStories) => [response.data, ...currentStories.filter((story) => story.slug !== response.data.slug)].slice(0, 12));
      toast.success("Story restored live in its original section.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Restore did not complete.");
    } finally {
      setRestoringArchiveId(null);
    }
  };

  const handleToggleFeatured = async (story) => {
    try {
      await axios.patch(`${API_BASE}/office/stories/${story.slug}/pin`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { is_featured: !story.is_featured },
      });
      await loadManagedStories();
      toast.success(story.is_featured ? "Story unpinned." : "Story pinned to the top.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Featured pinning did not complete.");
    }
  };

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16" data-testid="office-use-page">
      <NewsroomAdminNav />
      <section className="grid gap-8 border border-[rgba(10,15,26,0.12)] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] lg:grid-cols-[320px_minmax(0,1fr)] lg:gap-10 lg:p-8" data-testid="office-use-layout">
        <div className="space-y-6" data-testid="office-use-sidebar">
          <div>
            <p className="eyebrow text-red-600" data-testid="office-use-overline">Office use</p>
            <h1 className="headline-lg mt-3" data-testid="office-use-title">{editingStorySlug ? "Edit a current story." : "Publish a story directly to the website."}</h1>
          </div>
          {currentUser ? (
            <div className="border border-[rgba(10,15,26,0.12)] bg-white p-4" data-testid="office-use-current-user-panel">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500" data-testid="office-use-current-user-label">Signed in as</p>
              <p className="mt-2 text-sm font-semibold text-slate-900" data-testid="office-use-current-user-name">{currentUser.full_name}</p>
              <p className="mt-1 text-xs text-slate-600" data-testid="office-use-current-user-email">{currentUser.email}</p>
            </div>
          ) : null}
          <div className="border border-[rgba(10,15,26,0.12)] bg-slate-50 p-5" data-testid="office-use-help-box">
            <p className="text-sm leading-7 text-slate-700">You can now publish text-only posts, choose a main hero image or YouTube video, add supporting photos for the body, a bottom gallery, and a dedicated thumbnail. If no thumbnail is chosen, the default Content Watch placeholder will be used automatically.</p>
          </div>
          <div className="border border-[rgba(10,15,26,0.12)] bg-white p-4" data-testid="office-use-placeholder-panel">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Default thumbnail fallback</p>
            <div className="mt-3 overflow-hidden border border-[rgba(10,15,26,0.12)] bg-slate-100">
              <div className="aspect-square w-full max-w-[180px]">
                <img alt="Default Content Watch thumbnail placeholder" className="h-full w-full object-cover" src={PLACEHOLDER_THUMBNAIL} />
              </div>
            </div>
          </div>
          {editingStorySlug ? (
            <Button className="rounded-none border border-slate-950 bg-transparent px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-slate-950 hover:bg-slate-950 hover:text-white" data-testid="office-use-cancel-edit-button" onClick={() => {
              resetComposer();
              setAuthorName(currentUser?.first_name || currentUser?.full_name || "");
            }} type="button" variant="ghost">
              Cancel edit mode
            </Button>
          ) : null}
          <Button className="rounded-none border border-slate-950 bg-transparent px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-slate-950 hover:bg-slate-950 hover:text-white" data-testid="office-use-logout-button" onClick={() => { window.localStorage.removeItem("content_watch_token"); navigate("/newsroom-login"); }} variant="ghost">
            Log out
          </Button>
        </div>

        <form className="grid gap-4 sm:gap-5" data-testid="office-use-form" onSubmit={handlePublish}>
          <input className="border border-[rgba(10,15,26,0.12)] px-4 py-4 text-sm outline-none focus:border-slate-950" data-testid="office-use-headline-input" onChange={(event) => setHeadline(event.target.value)} placeholder="Story headline" required type="text" value={headline} />
          <input className="border border-[rgba(10,15,26,0.12)] px-4 py-4 text-sm outline-none focus:border-slate-950" data-testid="office-use-author-input" onChange={(event) => setAuthorName(event.target.value)} placeholder="Author name" required type="text" value={authorName} />
          <input className="border border-[rgba(10,15,26,0.12)] px-4 py-4 text-sm outline-none focus:border-slate-950" data-testid="office-use-photo-credit-input" onChange={(event) => { setPhotoCreditCustomized(true); setPhotoCredit(event.target.value); }} placeholder="Photo credit" type="text" value={photoCredit} />
          <select className="border border-[rgba(10,15,26,0.12)] px-4 py-4 text-sm outline-none focus:border-slate-950" data-testid="office-use-section-select" onChange={(event) => setSection(event.target.value)} value={section}>
            {SECTION_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <input className="border border-[rgba(10,15,26,0.12)] px-4 py-4 text-sm outline-none focus:border-slate-950" data-testid="office-use-published-at-input" onChange={(event) => setPublishedAt(event.target.value)} type="datetime-local" value={publishedAt} />
          <input className="border border-[rgba(10,15,26,0.12)] px-4 py-4 text-sm outline-none focus:border-slate-950" data-testid="office-use-location-input" onChange={(event) => setLocation(event.target.value)} placeholder="Location" required type="text" value={location} />
          <input className="border border-[rgba(10,15,26,0.12)] px-4 py-4 text-sm outline-none focus:border-slate-950" data-testid="office-use-tags-input" onChange={(event) => setTags(event.target.value)} placeholder="Tags, separated by commas" type="text" value={tags} />
          <input className="border border-[rgba(10,15,26,0.12)] px-4 py-4 text-sm outline-none focus:border-slate-950" data-testid="office-use-youtube-input" onChange={(event) => setYoutubeUrl(event.target.value)} placeholder="Optional main YouTube video link" type="url" value={youtubeUrl} />
          <div className="border border-[rgba(10,15,26,0.12)] bg-white" data-testid="office-use-editor-wrapper">
            <ReactQuill modules={{ toolbar: [[{ header: [1, 2, false] }], ["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["clean"]] }} onChange={setBodyHtml} placeholder="Paste the story from Google Docs here" theme="snow" value={bodyHtml} />
          </div>
          {editingStoryPreview ? (
            <div className="space-y-4 border border-[rgba(10,15,26,0.12)] bg-slate-50 px-4 py-4" data-testid="office-use-existing-media-panel">
              <p className="text-sm font-semibold text-slate-800">Current media on this story</p>
              {renderMediaPreview("Current hero", editingStoryPreview.image_url ? [editingStoryPreview.image_url] : [])}
              {renderMediaPreview("Current supporting", editingStoryPreview.supporting_image_gallery || [])}
              {editingStoryPreview.supporting_media_items?.length ? (
                <div className="space-y-2">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Current paragraph placements</p>
                  {editingStoryPreview.supporting_media_items.map((item, index) => (
                    <p key={`${item.asset_id}-${index}`} className="text-xs text-slate-600" data-testid={`office-use-existing-supporting-position-${index}`}>Supporting image {index + 1}: after paragraph {item.insert_after_paragraph || 1}</p>
                  ))}
                </div>
              ) : null}
              {renderMediaPreview("Current gallery", editingStoryPreview.summary_image_gallery || [])}
              {renderMediaPreview("Current thumbnail", [editingStoryPreview.thumbnail_url || PLACEHOLDER_THUMBNAIL])}
              <p className="text-xs leading-6 text-slate-500">Upload new files below only for the media groups you want to replace.</p>
            </div>
          ) : null}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4" data-testid="office-use-main-image-upload-field">
              <label className="block text-sm font-semibold text-slate-800" htmlFor="office-use-main-image">Main hero image</label>
              <input data-testid="office-use-main-image-input" id="office-use-main-image" onChange={(event) => setMainImage(event.target.files?.[0] ? createMediaItem(event.target.files[0]) : null)} type="file" accept="image/*" className="mt-3 block w-full text-sm text-slate-700" />
              {mainImageName ? <p className="mt-3 text-xs text-slate-500">{mainImageName}</p> : null}
              {mainImage ? <MediaOrganizerGrid dataTestId="office-use-main-image-organizer" items={[mainImage]} onMove={() => {}} onRemove={() => setMainImage(null)} title="Hero media queue" /> : null}
            </div>
            <div className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4" data-testid="office-use-thumbnail-upload-field">
              <label className="block text-sm font-semibold text-slate-800" htmlFor="office-use-thumbnail-image">Thumbnail image</label>
              <input data-testid="office-use-thumbnail-image-input" id="office-use-thumbnail-image" onChange={(event) => setThumbnailImage(event.target.files?.[0] ? createMediaItem(event.target.files[0]) : null)} type="file" accept="image/*" className="mt-3 block w-full text-sm text-slate-700" />
              <p className="mt-3 text-xs text-slate-500">{thumbnailImageName || "If none is chosen, the default Content Watch placeholder will be used."}</p>
              {thumbnailImage ? <MediaOrganizerGrid dataTestId="office-use-thumbnail-organizer" items={[thumbnailImage]} onMove={() => {}} onRemove={() => setThumbnailImage(null)} title="Thumbnail queue" /> : null}
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4" data-testid="office-use-supporting-images-upload-field">
              <label className="block text-sm font-semibold text-slate-800" htmlFor="office-use-supporting-images">Supporting images between the paragraphs</label>
              <input data-testid="office-use-supporting-images-input" id="office-use-supporting-images" multiple onChange={(event) => setSupportingImages(Array.from(event.target.files || []).map((file, index) => createMediaItem(file, { insertAfterParagraph: Math.min(index + 1, Math.max(paragraphCount, 1)) })))} type="file" accept="image/*" className="mt-3 block w-full text-sm text-slate-700" />
              {supportingImageNames.length ? <p className="mt-3 text-xs text-slate-500">{supportingImageNames.length} supporting photo(s) selected</p> : null}
              {supportingImages.length ? (
                <div className="mt-4">
                  <MediaOrganizerGrid
                    dataTestId="office-use-supporting-organizer"
                    items={supportingImages}
                    onMove={(sourceId, targetId) => setSupportingImages((currentItems) => reorderMediaItems(currentItems, sourceId, targetId))}
                    onPositionChange={(itemId, paragraph) => setSupportingImages((currentItems) => currentItems.map((item) => item.id === itemId ? { ...item, insertAfterParagraph: paragraph } : item))}
                    onRemove={(itemId) => setSupportingImages((currentItems) => currentItems.filter((item) => item.id !== itemId))}
                    paragraphOptions={paragraphOptions}
                    showParagraphSelect
                    title="Supporting image organizer"
                  />
                </div>
              ) : null}
            </div>
            <div className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4" data-testid="office-use-gallery-images-upload-field">
              <label className="block text-sm font-semibold text-slate-800" htmlFor="office-use-gallery-images">Bottom gallery images</label>
              <input data-testid="office-use-gallery-images-input" id="office-use-gallery-images" multiple onChange={(event) => setGalleryImages(Array.from(event.target.files || []).map((file) => createMediaItem(file)))} type="file" accept="image/*" className="mt-3 block w-full text-sm text-slate-700" />
              {galleryImageNames.length ? <p className="mt-3 text-xs text-slate-500">{galleryImageNames.length} bottom gallery photo(s) selected</p> : null}
              {galleryImages.length ? (
                <div className="mt-4">
                  <MediaOrganizerGrid
                    dataTestId="office-use-gallery-organizer"
                    items={galleryImages}
                    onMove={(sourceId, targetId) => setGalleryImages((currentItems) => reorderMediaItems(currentItems, sourceId, targetId))}
                    onRemove={(itemId) => setGalleryImages((currentItems) => currentItems.filter((item) => item.id !== itemId))}
                    title="Bottom gallery organizer"
                  />
                </div>
              ) : null}
            </div>
          </div>
          <Button className="rounded-none bg-[#D21404] px-5 py-6 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#B31203]" data-testid="office-use-publish-button" disabled={publishing} type="submit">
            {publishing ? (editingStorySlug ? "Updating" : "Publishing") : (editingStorySlug ? "Update Story" : "Publish Story")}
          </Button>
        </form>
      </section>
      {canEditStories ? (
        <section className="mt-10 border border-[rgba(10,15,26,0.12)] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] sm:p-8" data-testid="office-use-edit-panel">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="eyebrow text-red-600" data-testid="office-use-edit-panel-overline">Current posts</p>
              <h2 className="headline-lg mt-3" data-testid="office-use-edit-panel-title">Edit live stories from the newsroom desk.</h2>
            </div>
            <Button className="rounded-none border border-slate-950 bg-transparent px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-950 hover:bg-slate-950 hover:text-white" data-testid="office-use-refresh-stories-button" onClick={() => loadManagedStories()} type="button" variant="ghost">
              Refresh stories
            </Button>
          </div>
          <div className="mt-6 grid gap-4" data-testid="office-use-edit-story-list">
            {managedStories.map((story) => (
              <div key={story.slug} className="flex flex-col gap-4 border border-[rgba(10,15,26,0.08)] p-4 lg:flex-row lg:items-center lg:justify-between" data-testid={`office-use-edit-story-item-${story.slug}`}>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-red-600">{getStorySectionLabel(story.section)} · {getDeskStatus(story)}{story.is_featured ? " · featured" : ""}</p>
                  <p className="mt-2 font-['Outfit'] text-xl font-bold tracking-tight text-slate-950">{story.headline}</p>
                  <p className="mt-2 text-sm text-slate-600">{story.author || "No author"} · {toDateTimeLocal(story.published_at).replace("T", " ")}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button className="rounded-none border border-[#D21404] bg-transparent px-4 py-4 text-xs font-bold uppercase tracking-[0.18em] text-[#D21404] hover:bg-[#D21404] hover:text-white" data-testid={`office-use-featured-story-button-${story.slug}`} onClick={() => handleToggleFeatured(story)} type="button" variant="ghost">
                    {story.is_featured ? "Unpin featured" : "Pin featured"}
                  </Button>
                  <Button className="rounded-none bg-slate-950 px-4 py-4 text-xs font-bold uppercase tracking-[0.18em] text-white hover:bg-slate-800" data-testid={`office-use-edit-story-button-${story.slug}`} onClick={() => populateFormForEditing(story)} type="button">
                    Edit story
                  </Button>
                  <Button asChild className="rounded-none border border-slate-950 bg-transparent px-4 py-4 text-xs font-bold uppercase tracking-[0.18em] text-slate-950 hover:bg-slate-950 hover:text-white" data-testid={`office-use-view-story-button-${story.slug}`} type="button" variant="ghost">
                    <a href={`/stories/${story.slug}`}>View live</a>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {canEditStories && analytics ? (
        <section className="mt-10 border border-[rgba(10,15,26,0.12)] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] sm:p-8" data-testid="office-use-analytics-panel">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="eyebrow text-red-600" data-testid="office-use-analytics-overline">Newsroom analytics</p>
              <h2 className="headline-lg mt-3" data-testid="office-use-analytics-title">Fast read on output, desks, and investigation pressure.</h2>
            </div>
            <Button className="rounded-none border border-slate-950 bg-transparent px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-950 hover:bg-slate-950 hover:text-white" data-testid="office-use-refresh-analytics-button" onClick={() => loadControlRoom(token)} type="button" variant="ghost">
              Refresh analytics
            </Button>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="office-use-analytics-summary-grid">
            {(analytics.summary_cards || []).slice(0, 4).map((card) => (
              <article key={card.label} className="border border-[rgba(10,15,26,0.08)] bg-slate-50 p-4" data-testid={`office-use-analytics-card-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500"><ChartBar size={16} weight="fill" />{card.label}</p>
                <p className="mt-3 font-['Outfit'] text-3xl font-bold tracking-tight text-slate-950">{card.value}</p>
              </article>
            ))}
          </div>

          <div className="mt-8 grid gap-6 xl:grid-cols-3" data-testid="office-use-analytics-detail-grid">
            <div className="border border-[rgba(10,15,26,0.08)] bg-slate-50 p-5" data-testid="office-use-analytics-desk-mix">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Desk mix</p>
              <div className="mt-4 space-y-3">
                {(analytics.desk_mix || []).slice(0, 6).map((item) => (
                  <div key={item.label} className="flex items-center justify-between gap-4 border border-[rgba(10,15,26,0.08)] bg-white px-4 py-3" data-testid={`office-use-analytics-desk-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <span className="text-sm font-semibold text-slate-700">{item.label}</span>
                    <span className="text-sm font-bold uppercase tracking-[0.14em] text-slate-950">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-[rgba(10,15,26,0.08)] bg-slate-50 p-5" data-testid="office-use-analytics-entity-watch">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Entity watch</p>
              <div className="mt-4 space-y-3">
                {(analytics.entity_watch || []).slice(0, 5).map((item) => (
                  <div key={item.label} className="flex items-center justify-between gap-4 border border-[rgba(10,15,26,0.08)] bg-white px-4 py-3" data-testid={`office-use-analytics-entity-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <span className="text-sm font-semibold text-slate-700">{item.label}</span>
                    <span className="text-sm font-bold uppercase tracking-[0.14em] text-slate-950">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-[rgba(10,15,26,0.08)] bg-slate-50 p-5" data-testid="office-use-analytics-updates">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Recent updates</p>
              <div className="mt-4 space-y-3">
                {(analytics.recent_story_updates || []).map((story) => (
                  <div key={story.slug} className="border border-[rgba(10,15,26,0.08)] bg-white px-4 py-3" data-testid={`office-use-analytics-recent-${story.slug}`}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-red-600">{getStorySectionLabel(story.section)}</p>
                    <p className="mt-2 text-sm font-semibold text-slate-900">{story.headline}</p>
                    <p className="mt-2 text-xs uppercase tracking-[0.16em] text-slate-500">{toDateTimeLocal(story.updated_at).replace("T", " ")}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : null}
      {canDeleteStories ? <NewsroomControlPanels archivedStories={archivedStories} deletingSlug={deletingSlug} loading={loadingControlRoom} onDelete={handleDeleteStory} onRefresh={() => loadControlRoom(token)} onRestore={handleRestoreStory} restoringArchiveId={restoringArchiveId} stories={managedStories} /> : null}
    </main>
  );
};